import React, { useCallback, useEffect, useState } from 'react';
import { FormControl, InputLabel, Select, MenuItem, Input, Checkbox } from "@mui/material";
import Grid from '@mui/material/Grid2';
import {
  DisablableOption,
  ModelBase
} from "@models";

import CommonUtilities from "@utilities/common_utilities";
import ModalOrDrawer from "@components/display/ModalOrDrawer";
import DandDButtonBox from '@dndComponents/DandDButtonBoxSimple';
import DisplayStringWithReplacements from '@dndComponents/display/DisplayStringWithReplacements';
// import LoadingBox from "@components/input/LoadingBox";

interface Props {
  name: string;
  value?: string;
  values?: string[];
  options?: string[] | DisablableOption[] | null;
  extra_options?: any[];
  watch_me?: any[];
  display_map?: any;
  onChange?: Function | null; 
  onClose?: Function | null;
  multiple?: boolean;
  max_selected?: number;
  color?: string;
  disabled?: boolean;
  small?: boolean;
  ignore_disabled?: boolean;
  auto_sort?: boolean;
  log_it?: boolean;
  close_on_extra_clicked?: boolean;
  ignore_missing?: boolean;
  reload_time?: number;
  images?: boolean;
  show_current_order?: boolean;
  width?: number;
  allow_all?: boolean;
  allow_none?: boolean;
}

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
      width: 250,
    },
  },
};

const common_util = CommonUtilities.getInstance();

export default function SelectStringBox({
  name,
  value,
  values,
  options = null,
  extra_options = [],
  watch_me = [],
  display_map = null,
  onChange = null,
  onClose = null,
  // multiple = false,
  max_selected = -1,
  color = "",
  disabled = false,
  small = false,
  ignore_disabled = false,
  auto_sort = false,
  log_it = false,
  close_on_extra_clicked = false,
  ignore_missing = false,
  reload_time = 0,
  images = false,
  show_current_order = false,
  width = -1,
  allow_all = false,
  allow_none = false,
}: Props) {
  const [fetched, setFetched] = useState(false);
  const [my_values, setValues] = useState(values ? [...values] : []);
  const [prev_values, setPrevValues] = useState(values ? [...values] : []);
  const [my_options, setOptions] = useState<DisablableOption[]>([]);
  const [prev_options, setPrevOptions] = useState(options);
  const [open, setOpen] = useState(false);
  const [modal_open, setModalOpen] = useState(false);
  const [prev_reload_time, setReloadTime] = useState(reload_time);
  
  const widthFinder: any = React.createRef();

  const buildOptions = useCallback((): DisablableOption[] => {
    let new_options: DisablableOption[] = [];
    // if (log_it) {
    //   debugger;
    // }
    if (allow_all) {
      new_options.push({ id: "All", text: "All" });
    }
    if (allow_none) {
      new_options.push({ id: "None", text: "None" });
    }
    if (options) {
      for (const option of options) {
        if (typeof(option) === "string") {
          if (display_map) {
            new_options.push({ id: option, text: `${display_map[option]}`, image_url: `${display_map[option]}` });
          } else {
            new_options.push({ id: option, text: option, image_url: option });
          }
        } else {
          new_options.push(option);
        }
      }
      const new_extra_options: DisablableOption[] = [];
      for (const option of extra_options) {
        if (option instanceof ModelBase) {
          new_extra_options.push({ id: option._id, text: option.name, image_url: option.image_url });
        } else if (typeof(option) === "string") {
          if (display_map) {
            new_extra_options.push({ id: option, text: `${display_map[option]}`, image_url: `${display_map[option]}` });
          } else {
            new_extra_options.push({ id: option, text: option, image_url: option });
          }
        } else if (typeof(option) === "number") {
          new_extra_options.push({ id: `${option}`, text: `${option}` });
        } else if (option.id && option.text) { // It's a DisablableOption
          new_extra_options.push({ 
            id: option.id, 
            text: option.text, 
            disabled: option.disabled === undefined ? false : option.disabled, 
            reason: option.reason,
            image_url: option.image_url 
          });
        } else {
          console.log(option);
        }
      }
      if (auto_sort) {
        new_options = new_options.sort((a,b) => {return a.text.localeCompare(b.text)});
      }
      new_options = [...new_extra_options, ...new_options];
    }
    return new_options;
  }, [allow_all, allow_none, auto_sort, display_map, extra_options, options]);

  useEffect(() => {
    if (!fetched) {
      setOptions(buildOptions());
      setFetched(true);
    }
  }, [fetched, options, display_map, extra_options, allow_all, allow_none, auto_sort, log_it, setOptions, setFetched, buildOptions]);

  useEffect(() => {
    if (reload_time !== prev_reload_time) {
      setReloadTime(reload_time);
      setValues(values ? [...values] : []);
      setFetched(false);
    }
  }, [reload_time, prev_reload_time, values, setValues, setFetched]);
  
  useEffect(() => {
    if ((options !== prev_options && (options !== null || prev_options !== null)) || 
      (options !== null && prev_options !== null && !common_util.listsMatch(options, prev_options))) {
      if (log_it) {
        debugger;
      }
      setPrevOptions(options);
      setFetched(false);
    }
  }, [options, prev_options, log_it, setPrevOptions, setFetched]);

  useEffect(() => {
    if (values && !common_util.listsMatch(values, prev_values)) {
      setPrevValues(values);
      setValues(values);
    }
  }, [values, my_values, prev_values, setPrevValues, setValues]);

  const getTextForId = (id: string) => {
    const opt_finder = my_options.find(o => o.id === id);
    if (opt_finder) {
      return opt_finder.text;
    }
    return id;
  }

  const renderValueMultiple = (selected: string[]) => {
    if (images) {
      let items = selected.filter(str => !ignore_missing || my_options.find(o => o.id === str));
      if (!show_current_order) {
        items = items.sort((a,b) => { return a.localeCompare(b); });
      }
      return (
        <div style={{ width: (width - 40), overflowX: "hidden" }}>
          { items.map((id, key) => { 
            const opt = my_options.find(o => o.id === id);
            if (opt) {
              return (
                <span key={key}>
                  { opt.image_url && opt.image_url !== "" ? 
                    <img src={ opt.image_url } alt={name}
                      style={{
                        maxWidth: "10em",
                        maxHeight: "10em",
                        border: "1px solid"
                      }} 
                    />
                  : opt.text }
                </span>
              );
            }
            return null;
          })} 
        </div>
      );
    }
    return selected.filter(str => !ignore_missing || my_options.find(o => o.id === str)).map(id => { 
      return getTextForId(id); 
    }).join(', ');
  }

  const renderControl = () => {
    if (log_it) {
      // debugger;
      console.log(value);
      console.log(values);
      console.log(my_values);
      console.log(options);
    }
    if (small) {
      return (
        <div>
          <DandDButtonBox
            name={ !value || value === "" ? name : value }
            style={{ lineHeight: "1.4" }}
            disabled={disabled}
            onClick={() => {
              setModalOpen(true);
            }}
          />
          <ModalOrDrawer
            open={modal_open}
            onClose={() => {setModalOpen(false)}}
          >
            <Grid container spacing={0} direction="column">
              <Grid>
                <SelectStringBox
                  name={name}
                  value={value}
                  options={my_options}
                  onChange={(value: string) => {
                    if (onChange) {
                      onChange(value);
                      setModalOpen(false);
                    }
                  }}
                  onClose={(value: string) => {
                    if (onClose) {
                      onClose(value);
                      setModalOpen(false);
                    }
                  }}
                />
              </Grid>
            </Grid>
          </ModalOrDrawer>
        </div>
      );
    } else {
      if (values !== undefined) {
        const select = (
          <FormControl variant="outlined" fullWidth>
            <InputLabel htmlFor={`selectInput_${name}`}>{name}</InputLabel>
            <Select ref={widthFinder}
              labelId={`selectInput_${name}`}
              id={`selectInput_${name}`}
              open={open}
              onOpen={() => {
                setOpen(true);
              }}
              multiple
              multiline
              fullWidth
              disabled={disabled}
              value={my_values}
              onChange={(event: any) => {
                let new_open = true;
                // debugger;
                if (close_on_extra_clicked) {
                  // debugger;
                  const previous_overlaps = common_util.findOverlaps([...extra_options,...watch_me], my_values);
                  const new_overlaps = common_util.findOverlaps([...extra_options,...watch_me], event.target.value);
                  if (new_overlaps.length > previous_overlaps.length) {
                    new_open = false;
                  }
                }
                let new_values = event.target.value as string[];
                if (allow_all && new_values.includes("All")) {
                  new_values = my_options.filter(o => o.id !== "All" && o.id !== "None").map(o => o.id);
                } else if (allow_none && new_values.includes("None")) {
                  new_values = [];
                } 
                if (!new_open) {
                  if (onClose) {
                    onClose(new_values);
                  } else if (onChange) {
                    onChange(new_values);
                  }
                }
                setValues(new_values);
                setOpen(new_open);
              }}
              onClose={() => {
                setOpen(false);
                if (onClose) {
                  onClose(my_values);
                } else if (onChange) {
                  onChange(my_values);
                }
              }}
              input={<Input />}
              renderValue={renderValueMultiple}
              MenuProps={MenuProps}
            >
              { my_options.map(opt => {
                const color = (opt.color ? opt.color : undefined);
                let use_disabled = !ignore_disabled && opt.disabled;
                if (!use_disabled && !my_values.includes(opt.id) && max_selected > -1 && my_values.length >= max_selected) {
                  use_disabled = true;
                }
                if (log_it && opt.text.includes("Monk")) {
                  console.log(opt);
                }
                return (
                  <MenuItem key={opt.id} value={opt.id} disabled={ use_disabled }>
                    { opt.disabled ?
                      <Grid container spacing={0} direction="row">
                        { images && opt.image_url && opt.image_url !== "" ?
                          <Grid size={12} style={{ color }}>
                            <img src={ opt.image_url } alt={name}
                              style={{
                                maxWidth: "10em",
                                maxHeight: "10em",
                                border: "1px solid"
                              }} 
                            />
                            &nbsp;
                            { opt.text }
                          </Grid>
                        :
                          <Grid size={12} style={{ color }}>{ opt.text }</Grid>
                        }
                        { opt.reason &&
                          <Grid size={12} style={{ color: "red", fontSize: "8px" }}>{opt.reason}</Grid>
                        }
                        { opt.detail &&
                          <Grid size={12} className="small_label gray"><DisplayStringWithReplacements display_string={opt.detail} /></Grid>
                        }
                      </Grid>
                    : 
                      <Grid container spacing={0} direction="row">
                        <Grid size={12}>
                          <Checkbox checked={ my_values.includes(opt.id) } />
                          { images && opt.image_url && opt.image_url !== "" ? 
                            <>
                              <img src={ opt.image_url } alt={name}
                                style={{
                                  maxWidth: "10em",
                                  maxHeight: "10em",
                                  border: "1px solid"
                                }} 
                              />
                              &nbsp;
                              <span style={{ color }}>{ opt.text }</span>
                            </>
                          : 
                            <span style={{ color }}>{ opt.text }</span>
                          }
                          { log_it ? ` (${opt.id})` : "" }
                        </Grid>
                        { opt.reason &&
                          <Grid size={12} style={{ color: "red", fontSize: "8px" }}>{opt.reason}</Grid>
                        }
                        { opt.detail &&
                          <Grid size={12} className="small_label gray"><DisplayStringWithReplacements display_string={opt.detail} /></Grid>
                        }
                      </Grid>
                    }
                  </MenuItem>
                );
              })}
            </Select>
          </FormControl>
        );
        const orphaned = !ignore_missing && my_values.filter(o => 
          my_options.filter(o2 => o2.id === o).length === 0).length > 0;
        if (orphaned) {
          return (
            <Grid container spacing={0} direction="row">
              <Grid size={9}>{ select }</Grid>
              <Grid size={3}>
                <DandDButtonBox
                  name="Remove Orphaned Values"
                  onClick={() => {
                    const changed = values.filter(o => 
                      my_options.find(o2 => o2.id === o));
                    if (onClose) {
                      onClose(changed);
                    } else if (onChange) {
                      onChange(changed);
                    }
                    setValues(changed);
                  }}
                />
              </Grid>
            </Grid>
          );
        } else {
          return select;
        }
      } else if (value !== undefined) {
        return (
          <FormControl variant="outlined" fullWidth>
            <InputLabel htmlFor={`selectInput_${name}`}>{name}</InputLabel>
            <Select
              labelId={`selectInput_${name}`}
              id={`selectInput_${name}`}
              value={ value !== null ? `${value}` : ""}
              disabled={disabled}
              onChange={(event: any) => {
                if (onChange) {
                  onChange(event.target.value);
                } else if (onClose) {
                  onClose(event.target.value);
                }
              }}
              fullWidth
              // labelWidth={ labelWidth ? labelWidth : labelWidth }
            >
              { my_options.map(opt => {
                const color = (opt.color ? opt.color : undefined);
                return (
                  <MenuItem key={opt.id} value={opt.id} disabled={ !ignore_disabled && opt.disabled }>
                    { opt.disabled ?
                      <Grid container spacing={0} direction="column">
                        { images && opt.image_url && opt.image_url !== "" ? 
                          <Grid>
                            <img src={ opt.image_url } alt={name}
                              style={{
                                maxWidth: "10em",
                                maxHeight: "10em",
                                border: "1px solid"
                              }} 
                            />
                          </Grid>
                          : <Grid style={{ color }}>{opt.text}</Grid>
                        }
                        <Grid style={{ color: "red", fontSize: "8px" }}>{opt.reason}</Grid>
                        { opt.detail &&
                          <Grid size={12} className="small_label gray"><DisplayStringWithReplacements display_string={opt.detail} /></Grid>
                        }
                      </Grid>
                    : images && opt.image_url && opt.image_url !== "" ? 
                      <Grid container spacing={0} direction="column">
                        <Grid size={12}>
                          <img src={ opt.image_url } alt={name}
                            style={{
                              maxWidth: "10em",
                              maxHeight: "10em",
                              border: "1px solid"
                            }} 
                          />
                        </Grid>
                        { opt.detail &&
                          <Grid size={12} className="small_label gray"><DisplayStringWithReplacements display_string={opt.detail} /></Grid>
                        }
                      </Grid>
                    : 
                      <Grid container spacing={0} direction="column">
                        <Grid size={12} style={{ color }}>{opt.text}</Grid>
                        { opt.detail &&
                          <Grid size={12} className="small_label gray"><DisplayStringWithReplacements display_string={opt.detail} /></Grid>
                        }
                      </Grid>
                    }
                  </MenuItem>
                );
              })}
            </Select>
          </FormControl>
        );
      } else {
        return <></>
      }
    }
  }

  // if (!fetched) {
  //   return (<LoadingBox />);
  // } else 
  if (color !== "") {
    return (
      <div style={{
        border: "1px solid #1C9AEF"
      }}>
        { renderControl() }
      </div>
    );
  } else {
    return renderControl();
  }
}
